// src\components\Payroll\PayrollSalaryBatchFld\shared\PartialSalaryBatchModal.js
import React, { useState, useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { Select } from "../../../../common/Select";
import { Input, DateInput } from "../../../../common/Input";
import { BatchDetailSection } from "../sections/BatchDetailSection";
import { ConfirmationModal } from "../../../../common/Modal";
import { LoadingSpinner } from "../../../../common/Loading";
import { StatusBadge } from "../../../../common/Badge";
import { VesselPickerField } from "../../../../common/selectComponents/VesselPicker"; // ADDED

// ── Month name lookup — maps JS Date month index (0–11) to name string ────────
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ── Empty form ────────────────────────────────────────────────────────────────
const emptyForm = {
  payrollPeriodId: "",
  effectiveDate: "",
  status: null,
  partialSalaryBatchDetails: [],
  vesselId: "",
};

// ── Modal ─────────────────────────────────────────────────────────────────────
export const PartialSalaryBatchModal = ({
  isOpen,
  onClose,
  mode,           // "add" | "edit" | "view"
  selectedData,
  options,
  dropdownsLoading,
  onAdd,
  onUpdate,
  showGeneralMessage
}) => {
  const isViewOnly = mode === "view";
  const canSave = !isViewOnly;

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [vesselDisplayName, setVesselDisplayName] = useState(""); // ADDED

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({
    title: "",
    message: "",
    action: null,
  });

  // ── Sync form when modal opens ────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setErrors({});
      if (selectedData) {
        setForm({
          ...emptyForm,
          payrollPeriodId: selectedData.payrollPeriodId || "",
          effectiveDate: selectedData.effectiveDate?.split("T")[0] || "",
          status: selectedData.status || null,
          vesselId: selectedData.vesselId || "", // MODIFIED: moved to top-level where it belongs
          partialSalaryBatchDetails: (selectedData.partialSalaryBatchDetails || []).map((d) => ({
            id: d.id || null,
            partialSalaryId: d.partialSalaryId || "",
            employeeType: d.employeeType || "",
            position: d.position || "",
            amount: d.amount ?? 0,
            isActive: d.isActive ?? true,
          })),
        });
        // ADDED: resolve display name from options so the picker shows the vessel name on edit/view
        const matchedVessel = (options?.vessels || []).find(
          (v) => String(v.value) === String(selectedData.vesselId)
        );
        setVesselDisplayName(matchedVessel?.label || selectedData.vesselName || "");
      } else {
        setForm(emptyForm);
        setVesselDisplayName(""); // ADDED
      }
    }
  }, [isOpen, selectedData]);

  // ── Field setter — clears error on change ─────────────────────────────────
  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // ── Derive identifier from selected payrollPeriodId ───────────────────────
  const payrollPeriodIdentifier = useMemo(() => {
    if (!form.payrollPeriodId || !options?.payrollPeriods?.length) return "";
    const match = options.payrollPeriods.find(
      (p) => String(p.value) === String(form.payrollPeriodId)
    );
    return match?.identifier || "";
  }, [form.payrollPeriodId, options?.payrollPeriods]);

  // ── Validate ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};

    if (!form.payrollPeriodId) e.payrollPeriodId = "Payroll Period is required.";

    if (!form.vesselId) e.vesselId = "Vessel is required."; // ADDED

    if (!form.effectiveDate) {
      e.effectiveDate = "Effective Date is required.";
    } else {
      const effectiveDate = new Date(form.effectiveDate);

      // Check: effective date must be earlier than the selected period's DateFrom
      const selectedPeriod = options?.payrollPeriods?.find(
        (p) => String(p.value) === String(form.payrollPeriodId)
      );
      

      // ADDED: Check effective date month against active PartialSalarySchedule months
      // Only run this check if the date is otherwise valid (no prior error set)
      if (!e.effectiveDate) {
        const effectiveMonthName = MONTH_NAMES[effectiveDate.getMonth()];
        const activeScheduleMonths = options?.activeSchedules || [];

        if (activeScheduleMonths.length > 0 && !activeScheduleMonths.includes(effectiveMonthName)) {
          e.effectiveDate = 'Invalid date, Please choose a date within an allowed month.';
        }
      }
    }

    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    if (mode === "add") {
      setConfirmationConfig({
        title: "Create Partial Salary Batch",
        message: "Save this partial salary batch?",
        action: doAdd,
      });
    } else {
      setConfirmationConfig({
        title: "Update Partial Salary Batch",
        message: "Update this partial salary batch?",
        action: doUpdate,
      });
    }
    setShowConfirmation(true);
  };

  const doAdd = async () => {
    setConfirmLoading(true);
    await onAdd(form, () => { setShowConfirmation(false); onClose(); });
    setConfirmLoading(false);
  };

  const doUpdate = async () => {
    setConfirmLoading(true);
    await onUpdate(form, selectedData.id, () => { setShowConfirmation(false); onClose(); });
    setConfirmLoading(false);
  };

  if (!isOpen) return null;

  const sharedSectionProps = { form, set, options, isViewOnly, errors, showGeneralMessage };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[95%] lg:w-[75%] xl:w-[65%] rounded-lg shadow-2xl flex flex-col h-[90vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-3 border-b bg-gray-50 rounded-t-lg">
          <h2 className="text-lg font-bold text-blue-900 flex items-center gap-2">
            <ReceiptLongIcon />
            {isViewOnly
              ? "View Partial Salary Batch"
              : mode === "add"
                ? "Add Partial Salary Batch"
                : "Edit Partial Salary Batch"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow bg-white space-y-6">

          {/* ── Batch Info Section ──────────────────────────────────────────── */}
          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Batch Information
              </span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${!isViewOnly
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-gray-100 text-gray-400 border-gray-200"
                  }`}
              >
                {!isViewOnly ? "Editable" : "View Only"}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4">
                <Select
                  label="Payroll Period:"
                  options={options?.payrollPeriods || []}
                  value={form.payrollPeriodId || ""}
                  onChange={(e) => set("payrollPeriodId", e.target.value)}
                  disabled={!canSave}
                  isRequired={true}
                  error={!!errors.payrollPeriodId}
                  errorMessage={errors.payrollPeriodId}
                />

                {/* Auto-populated identifier */}
                <Input
                  label="Payroll Period Cutoff:"
                  value={payrollPeriodIdentifier}
                  disabled={true}
                  placeholder="-- Auto-populated --"
                />
              </div>

              {/* REPLACED: Vessel Select → VesselPickerField */}
              <VesselPickerField
                label="Vessel:"
                vesselId={form.vesselId}
                displayValue={vesselDisplayName}
                onSelect={(vessel) => {
                  set("vesselId", vessel.id);
                  setVesselDisplayName(vessel.code);
                }}
                onClear={() => {
                  set("vesselId", "");
                  setVesselDisplayName("");
                }}
                allowClear={canSave}
                isRequired={true}
                disabled={!canSave}
                error={!!errors.vesselId}
                errorMessage={errors.vesselId}
              />

              <DateInput
                label="Effective Date:"
                value={form.effectiveDate || ""}
                onChange={(e) => set("effectiveDate", e.target.value)}
                disabled={!canSave}
                isRequired={true}
                error={!!errors.effectiveDate}
                errorMessage={errors.effectiveDate}
              />

              {/* Status — read-only */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">Status:</label>
                <div className="mt-1">
                  <StatusBadge status={form.status} />
                </div>
              </div>
            </div>

            {/* ADDED: Active schedule months hint — helps user know what months are valid */}
            {canSave && options?.activeSchedules?.length > 0 && (
              <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                <span className="font-semibold text-blue-600 shrink-0">Allowed months:</span>
                <span>{options.activeSchedules.join(", ")}</span>
              </div>
            )}

            {/* ADDED: Warning if no active schedules defined */}
            {canSave && options?.activeSchedules?.length === 0 && (
              <div className="mt-4 flex items-start gap-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                <span className="font-semibold shrink-0">⚠ No active schedule:</span>
                <span>No active months are defined in the Partial Salary Schedule. Please configure the schedule before creating a batch.</span>
              </div>
            )}
          </div>

          {/* ── Batch Details Section ───────────────────────────────────────── */}
          <BatchDetailSection {...sharedSectionProps} />
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end items-center gap-2 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-400 text-white rounded cursor-pointer hover:bg-gray-500 border-none transition-colors"
          >
            Close
          </button>
          {canSave && (
            <button
              onClick={handleSubmit}
              className="px-8 py-2 bg-blue-600 text-white rounded font-bold cursor-pointer hover:bg-blue-700 border-none transition-colors"
            >
              {mode === "add" ? "Save Batch" : "Update Batch"}
            </button>
          )}
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={confirmationConfig.action}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        isLoading={confirmLoading}
      />
      <LoadingSpinner loading={dropdownsLoading} />
    </div>
  );
};